import { ButtonLink } from "./Button";
import { HOME_SECTIONS } from "../lib/routes";
import { sellerSubmitUrl } from "../lib/seller";
import styles from "./ColdStartInvite.module.css";

/**
 * The invitation over the cold-start board (`WEB-023`, canvas artboards 6–9).
 *
 * `WEB-016` put two blocks in the empty board: an explanation addressed to the buyer and,
 * beneath it, an ask addressed to the founder. The cold-start canvas merges them into one
 * card, and the merge is the point — a founder reading the board illustration needs the
 * explanation and the ask in the same breath, not one above a rule from the other. The card
 * still leads with why the board is empty, so the buyer is still answered first.
 *
 * **What it may offer is fixed by canon, and it is short.** How the board actually fills:
 * founders submit, Dealbid reviews and approves **by hand, one at a time**, and while the
 * board holds ten or fewer deals every approved deal is listed free in approval order with no
 * auction running (`D-011` § 1). Then the permanent thing — `D-009`'s wall, where the first
 * ten approved deals are named for as long as the page exists.
 *
 * **What it must not offer is why this copy is centralised.** Canon § 1 declines to promise
 * visits, traffic, leads, customers or sales in the product thesis, and recruitment copy is
 * exactly where that promise creeps back in, one well-meant edit at a time. There is no count
 * of remaining places, no countdown and no urgency device (`AGENTS.md` § 11, `D-009`): a
 * remaining figure is false the moment the first deal is approved, which is the day this copy
 * is most likely to be read. `test/components/founder-invite.test.tsx` holds all of it.
 *
 * "By hand" describes **review and approval**, which is what actually happens. It never
 * describes the board as curated or hand-picked: canon § 12 and `AGENTS.md` § 14 rank the
 * board by bid, so a curation claim would become false the day the auction opens.
 *
 * The cohort is "the first ten approved deals" — canon § 4's set. Never "the first ten
 * bidders", which is different and smaller, since a deal can be approved and never bid.
 *
 * ## It stays while the board is filling, and moves when it does (`WEB-040`)
 *
 * `WEB-023` showed this card on an *empty* board only, so the first approval replaced a
 * drawing of ten places with a single row and took the explanation away at the moment it
 * became easiest to misread — one deal on a ten-place board, with nothing saying why. It now
 * stays for the whole of canon § 6's range and disappears when the board is full.
 *
 * Where it sits follows from that. With no deals it is **over** the drawing, which is
 * `WEB-023`'s canvas and says "this card is what the board is". With deals above it, it is
 * **in the flow** beneath them: a buyer came for the deals (canon § 13) and meets them first,
 * and the card then reads as the boundary between the board and the places it has not filled.
 * `overlay` is that difference and nothing else.
 *
 * **The count of filled positions is deliberately not in here.** The board states it, in its
 * own capacity line, which is a description of the board; this is recruitment copy, and a
 * figure inside recruitment copy reads as scarcity whichever direction it counts.
 * `test/components/founder-invite.test.tsx` refuses every `N of M` in this card, in both
 * states, and that guard is the reason the sentence lives out there instead.
 */
export function ColdStartInvite({ overlay = true }: { overlay?: boolean }) {
  return (
    <div className={`${styles.invite} ${overlay ? styles.overlay : styles.inflow}`}>
      {/*
        A level-2 heading under the board's level-1. The card is a titled region inside the
        board section rather than a note attached to it, which is what the canvas draws and
        what the document outline should say.
      */}
      <h2 className={styles.title}>The board opens with its first ten approved deals</h2>
      <p className={styles.body}>
        Dealbid is new, and this board fills the only way it can: with deals founders submit
        and Dealbid reviews and approves by hand, one at a time. While the board holds ten or
        fewer deals, every approved deal is listed free, in the order it was approved — no
        auction runs. The first ten approved deals are Founding Deals, they keep that badge
        permanently, and they are named permanently on the Founding Deals wall at the foot of
        this page.
      </p>
      {/*
        The mechanism, and then the sentence that keeps it a mechanism. Canon § 1 declines to
        promise an outcome in the product thesis, and a card written to recruit founders is
        the one place on the site where that promise would be easiest to make by accident.
        `WEB-016` established the refusal and `test/components/founder-invite.test.tsx` pins
        it; the cold-start canvas did not draw it, and it stays anyway.
      */}
      <p className={styles.body}>
        The mechanism is the whole of it: you post a genuine deal, buyers read the board, and
        whoever wants your deal opens it on your own site. Dealbid does not promise that
        anyone will.
      </p>
      <div className={styles.actions}>
        {/*
          The same destination the masthead, the auction section and the wall use
          (`WEB-014`), built by `src/lib/seller.ts`. A second path invented for this state
          would be a second thing to keep in step.
        */}
        <ButtonLink href={sellerSubmitUrl()}>Submit a deal</ButtonLink>
        <a className={styles.secondary} href={HOME_SECTIONS.howItWorks}>
          How it works ↓
        </a>
      </div>
      {/*
        Said in words, not left to the artwork. The drawn rows are the one thing on the page a
        reader could mistake for inventory Dealbid is holding back, and `AGENTS.md` § 11 does
        not allow that reading to depend on noticing a dashed border.

        "Behind" and "below" are not interchangeable here: the sentence has to describe where
        the rows actually are, and a card sitting under three real deals that claims rows
        behind it is pointing at the deals.
      */}
      <p className={`${styles.note} figure`}>
        {overlay
          ? "The rows behind this card are a drawing of the board, not deals."
          : "The rows below this card are a drawing of the board’s remaining positions, not deals."}
      </p>
    </div>
  );
}
